import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Sidebar } from './components/ui/Sidebar';
import { Header } from './components/ui/Header';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Leads } from './pages/Leads';
import { Grupos } from './pages/Grupos';
import { EmpresasList } from './pages/EmpresasList';
import { EmpresaPerfilTecnico } from './pages/EmpresaPerfilTecnico';
import { FunilVarredura } from './pages/FunilVarredura';
import { FeedProatividadePage } from './pages/FeedProatividade';
import { Licitacoes } from './pages/Licitacoes';
import { Calendario } from './pages/Calendario';
import { Noticias } from './pages/Noticias';
import { AssistenteIA } from './pages/AssistenteIA';
import { RevisaoMatches } from './pages/RevisaoMatches';
import { Usuarios } from './pages/Usuarios';
import { Perfil } from './pages/Perfil';
import { MercadoInteligencia } from './pages/MercadoInteligencia';
import { GestaoLicitacoes } from './pages/GestaoLicitacoes';
import { AndamentoProcessos } from './pages/AndamentoProcessos';
import { OraculoEdital } from './pages/agentes-ia/OraculoEdital';
import { RedatorNinja } from './pages/agentes-ia/RedatorNinja';
import { GuardiaoDisputa } from './pages/agentes-ia/GuardiaoDisputa';
import { Auditor360 } from './pages/agentes-ia/Auditor360';
import { Ferramentas } from './pages/agentes-ia/Ferramentas';
import { CentralPlanilhas } from './pages/agentes-ia/CentralPlanilhas';
import { useAuth } from './hooks/useAuth';
import { usePortalAuth } from './hooks/usePortalAuth';
import { useThemeSync } from './hooks/useThemeSync';
import { useViewModeStore } from './stores/view-mode';
import { onAuthHydrated } from './stores/auth';
import { onPortalAuthHydrated } from './stores/portal-auth';
const PortalLogin = lazy(() => import('./portal/PortalLogin').then((module) => ({ default: module.PortalLogin })));
const PortalLayout = lazy(() => import('./portal/PortalLayout').then((module) => ({ default: module.PortalLayout })));
const PortalDashboard = lazy(() => import('./portal/PortalDashboard').then((module) => ({ default: module.PortalDashboard })));
const PortalFeed = lazy(() => import('./portal/PortalFeed').then((module) => ({ default: module.PortalFeed })));
const PortalInteligencia = lazy(() => import('./portal/PortalInteligencia').then((module) => ({ default: module.PortalInteligencia })));
const PortalContratos = lazy(() => import('./portal/PortalContratos').then((module) => ({ default: module.PortalContratos })));
const PortalContratoDetalhe = lazy(() => import('./portal/PortalContratoDetalhe').then((module) => ({ default: module.PortalContratoDetalhe })));
const PortalRoboLances = lazy(() => import('./portal/PortalRoboLances').then((module) => ({ default: module.PortalRoboLances })));
const PortalDocumentos = lazy(() => import('./portal/PortalDocumentos').then((module) => ({ default: module.PortalDocumentos })));
const PortalComingSoon = lazy(() => import('./portal/PortalComingSoon').then((module) => ({ default: module.PortalComingSoon })));
const PortalMinhaEmpresa = lazy(() => import('./portal/PortalMinhaEmpresa').then((module) => ({ default: module.PortalMinhaEmpresa })));
const PortalOportunidadeDetalhe = lazy(() => import('./portal/PortalOportunidadeDetalhe').then((module) => ({ default: module.PortalOportunidadeDetalhe })));
const PortalLicitacoes = lazy(() => import('./portal/PortalLicitacoes').then((module) => ({ default: module.PortalLicitacoes })));
const PortalLicitacaoWorkspace = lazy(() => import('./portal/PortalLicitacaoWorkspace').then((module) => ({ default: module.PortalLicitacaoWorkspace })));
const PortalCalendario = lazy(() => import('./portal/PortalCalendario').then((module) => ({ default: module.PortalCalendario })));

function RouteFallback() {
  return <div className="min-h-screen bg-ds-bg flex items-center justify-center" aria-label="Carregando página"><div className="h-10 w-10 rounded-full border-2 border-ds-border border-t-ds-success animate-spin" /></div>;
}

function AppLayout({ children }: { children: React.ReactNode }) {
  useThemeSync();

  return (
    <div className="flex h-screen bg-ds-bg">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * Root path ("/"). In Visão Empresa, Meu Feed is the landing view instead of
 * the consultor Dashboard; without a selected preview empresa there is
 * nothing to redirect to, so it falls back to the Dashboard.
 */
function RootRoute() {
  const { mode, previewEmpresaId } = useViewModeStore();

  if (mode === 'empresa' && previewEmpresaId !== null) {
    return <Navigate to={`/empresas/${previewEmpresaId}/feed`} replace />;
  }

  return <Dashboard />;
}

function LegacyChatProcessosRedirect() {
  const { search } = useLocation();
  const empresaId = new URLSearchParams(search).get('empresa');
  const destination = empresaId ? `/gestao-licitacoes?empresa=${encodeURIComponent(empresaId)}` : '/gestao-licitacoes';

  return <Navigate to={destination} replace />;
}

function EmpresaContratosPreview() {
  const { id } = useParams<{ id: string }>();
  return <PortalContratos empresaId={Number(id)} />;
}

function EmpresaContratoDetalhePreview() {
  return <PortalContratoDetalhe />;
}

function EmpresaInteligenciaPreview() {
  const { id } = useParams<{ id: string }>();
  return <PortalInteligencia empresaId={Number(id)} />;
}

function EmpresaRoboLancesPreview() {
  const { id } = useParams<{ id: string }>();
  return <PortalRoboLances empresaId={Number(id)} />;
}

function EmpresaDocumentosPreview() {
  const { id } = useParams<{ id: string }>();
  return <PortalDocumentos empresaId={Number(id)} />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, initializeAuth } = useAuth();
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    const unsub = onAuthHydrated(() => {
      setHasHydrated(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (hasHydrated && !isInitialized) {
      void initializeAuth();
    }
  }, [hasHydrated, initializeAuth, isInitialized]);

  if (!hasHydrated || !isInitialized) {
    return (
      <div className="min-h-screen bg-ds-bg flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-ds-border border-t-ds-success animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

function PortalProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, initializeAuth } = usePortalAuth();
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    const unsub = onPortalAuthHydrated(() => {
      setHasHydrated(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (hasHydrated && !isInitialized) {
      void initializeAuth();
    }
  }, [hasHydrated, initializeAuth, isInitialized]);

  if (!hasHydrated || !isInitialized) {
    return (
      <div className="min-h-screen bg-ds-bg flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-ds-border border-t-ds-success animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  return <PortalLayout>{children}</PortalLayout>;
}

function App() {
  return (
    <Router>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <RootRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads"
          element={
            <ProtectedRoute>
              <Leads />
            </ProtectedRoute>
          }
        />
        <Route
          path="/grupos"
          element={
            <ProtectedRoute>
              <Grupos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas"
          element={
            <ProtectedRoute>
              <EmpresasList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas/:id/perfil-tecnico"
          element={
            <ProtectedRoute>
              <EmpresaPerfilTecnico />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas/:id/funil/varredura"
          element={
            <ProtectedRoute>
              <FunilVarredura />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas/:id/feed"
          element={
            <ProtectedRoute>
              <FeedProatividadePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas/:id/contratos"
          element={<ProtectedRoute><EmpresaContratosPreview /></ProtectedRoute>}
        />
        <Route
          path="/empresas/:empresaId/contratos/:id"
          element={<ProtectedRoute><EmpresaContratoDetalhePreview /></ProtectedRoute>}
        />
        <Route
          path="/empresas/:id/documentos"
          element={<ProtectedRoute><EmpresaDocumentosPreview /></ProtectedRoute>}
        />
        <Route
          path="/empresas/:id/robo-lances"
          element={<ProtectedRoute><EmpresaRoboLancesPreview /></ProtectedRoute>}
        />
        <Route
          path="/licitacoes"
          element={
            <ProtectedRoute>
              <Licitacoes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendario"
          element={
            <ProtectedRoute>
              <Calendario />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat-processo"
          element={
            <ProtectedRoute>
              <LegacyChatProcessosRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/noticias"
          element={
            <ProtectedRoute>
              <Noticias />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assistente-ia"
          element={
            <ProtectedRoute>
              <AssistenteIA />
            </ProtectedRoute>
          }
        />
        <Route
          path="/revisao-matches"
          element={
            <ProtectedRoute>
              <RevisaoMatches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute>
              <Usuarios />
            </ProtectedRoute>
          }
        />
        <Route
          path="/perfil"
          element={<ProtectedRoute><Perfil /></ProtectedRoute>}
        />
        <Route
          path="/gestao-licitacoes"
          element={
            <ProtectedRoute>
              <GestaoLicitacoes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/andamento-processos"
          element={
            <ProtectedRoute>
              <AndamentoProcessos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agentes-ia/oraculo-edital"
          element={
            <ProtectedRoute>
              <OraculoEdital />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agentes-ia/redator-ninja"
          element={
            <ProtectedRoute>
              <RedatorNinja />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agentes-ia/guardiao-disputa"
          element={
            <ProtectedRoute>
              <GuardiaoDisputa />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agentes-ia/ferramentas"
          element={<ProtectedRoute><Ferramentas /></ProtectedRoute>}
        />
        <Route
          path="/mercado-inteligencia"
          element={<ProtectedRoute><MercadoInteligencia /></ProtectedRoute>}
        />
        <Route
          path="/agentes-ia/auditor-360"
          element={
            <ProtectedRoute>
              <Auditor360 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agentes-ia/central-planilhas"
          element={
            <ProtectedRoute>
              <CentralPlanilhas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal"
          element={
            <PortalProtectedRoute>
              <PortalDashboard />
            </PortalProtectedRoute>
          }
        />
        <Route path="/portal/feed" element={<PortalProtectedRoute><PortalFeed /></PortalProtectedRoute>} />
        <Route path="/portal/inteligencia" element={<PortalProtectedRoute><PortalInteligencia /></PortalProtectedRoute>} />
        <Route path="/empresas/:id/inteligencia" element={<ProtectedRoute><EmpresaInteligenciaPreview /></ProtectedRoute>} />
        <Route path="/portal/contratos" element={<PortalProtectedRoute><PortalContratos /></PortalProtectedRoute>} />
        <Route path="/portal/contratos/:id" element={<PortalProtectedRoute><PortalContratoDetalhe /></PortalProtectedRoute>} />
        <Route path="/portal/robo-lances" element={<PortalProtectedRoute><PortalRoboLances /></PortalProtectedRoute>} />
        <Route path="/portal/oportunidades/:id" element={<PortalProtectedRoute><PortalOportunidadeDetalhe /></PortalProtectedRoute>} />
        <Route path="/portal/documentos" element={<PortalProtectedRoute><PortalDocumentos /></PortalProtectedRoute>} />
        <Route path="/portal/mercado" element={<PortalProtectedRoute><MercadoInteligencia portal /></PortalProtectedRoute>} />
        <Route path="/portal/funil" element={<PortalProtectedRoute><Navigate to="/portal/licitacoes" replace /></PortalProtectedRoute>} />
        <Route path="/portal/licitacoes" element={<PortalProtectedRoute><PortalLicitacoes /></PortalProtectedRoute>} />
        <Route path="/portal/licitacoes/:id" element={<PortalProtectedRoute><PortalLicitacaoWorkspace /></PortalProtectedRoute>} />
        <Route path="/portal/calendario" element={<PortalProtectedRoute><PortalCalendario /></PortalProtectedRoute>} />
        <Route path="/portal/minha-empresa" element={<PortalProtectedRoute><PortalMinhaEmpresa /></PortalProtectedRoute>} />
        <Route path="/portal/ajuda" element={<PortalProtectedRoute><PortalComingSoon title="Ajuda" /></PortalProtectedRoute>} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
